/**
 * ChatDealSelectionCard — inline deal picker triggered by request_deal_selection.
 *
 * AI Chat V1.5. Driven by the `deal_selection_prompt` SSE event (NOT a
 * tool_result — not registered in componentRegistry). Rendered below the
 * assistant message when `store.dealSelectionPrompt !== null`.
 *
 * Click flow:
 *   1. setDealContext({ deal_id, deal_name }) — locks context immediately
 *   2. clearDealSelectionPrompt()
 *   3. send(synthetic confirmation) — agent resumes with explicit context
 *
 * No navigation links anywhere — users stay in chat. If they want to open
 * the deal page, the model uses navigate_to(destination='deal_detail').
 *
 * Brand book: no rounded corners, semantic Tailwind tokens, sentence case,
 * Feather icons (none here — clean text-only rows).
 */
import { Badge, FinancialNumber, ScoreDisplay } from '../../../components/ui';
import {
  useChatStore,
  type SelectableDeal,
} from '../../../stores/chatStore';
import { useChatStream } from '../hooks/useChatStream';

const PURPOSE_LABELS: Record<string, string> = {
  acquisition: 'Acquisition',
  equipment: 'Equipment',
  refinancing: 'Refinancing',
  working_capital: 'Working capital',
  real_estate: 'Real estate',
  expansion: 'Growth',
  other: 'Other',
};

export function ChatDealSelectionCard() {
  const prompt = useChatStore((s) => s.dealSelectionPrompt);
  const setDealContext = useChatStore((s) => s.setDealContext);
  const clearDealSelectionPrompt = useChatStore(
    (s) => s.clearDealSelectionPrompt
  );
  const resolveDealSelection = useChatStore((s) => s.resolveDealSelection);
  const { send } = useChatStream();

  if (!prompt) {return null;}

  const handleSelect = (deal: SelectableDeal) => {
    setDealContext({
      deal_id: deal.id,
      deal_name: deal.deal_name ?? undefined,
    });
    // Replace the picker with a persistent compact confirmation of the pick
    // (rendered in ChatPanel from dealSelectionResolved) instead of clearing
    // the card outright — so the chosen deal remains in the chat.
    resolveDealSelection(deal);
    const name = deal.deal_name ?? `Deal #${deal.id}`;
    // V1.4.1 — synthetic: hidden from the chat thread, sent to the
    // backend so the model gets the explicit deal_id binding.
    send({
      text:
        `I want to discuss deal: "${name}" (deal_id: ${deal.id}). ` +
        `Please confirm and continue.`,
      synthetic: true,
    });
  };

  if (prompt.deals.length === 0) {
    return (
      <div className="mt-2 border border-border bg-surface-subtle px-3 py-3">
        <p className="text-body-sm text-txt-secondary">
          No active deals found. Create one first and then come back.
        </p>
        <button
          type="button"
          onClick={() => {
            clearDealSelectionPrompt();
            send({ text: 'create a deal' });
          }}
          className="mt-3 w-full border border-orange bg-surface-card px-3 py-2 text-body-sm text-orange transition-colors hover:bg-orange hover:text-surface-card"
        >
          Create your first deal →
        </button>
      </div>
    );
  }

  return (
    <div className="mt-2 border border-border bg-surface-card">
      <div className="border-b border-border px-3 py-2">
        <p className="text-body-sm text-txt-secondary">
          Select a deal to continue
        </p>
      </div>
      <div className="custom-scrollbar flex max-h-[360px] flex-col divide-y divide-border overflow-y-auto">
        {prompt.deals.map((deal) => (
          <DealSelectRow key={deal.id} deal={deal} onSelect={handleSelect} />
        ))}
      </div>
    </div>
  );
}

function DealSelectRow({
  deal,
  onSelect,
}: {
  deal: SelectableDeal;
  onSelect: (deal: SelectableDeal) => void;
}) {
  const purposeLabel = deal.financing_purpose
    ? (PURPOSE_LABELS[deal.financing_purpose] ?? deal.financing_purpose)
    : null;

  return (
    <button
      type="button"
      onClick={() => onSelect(deal)}
      className="flex w-full items-start gap-3 px-3 py-3 text-left transition-colors hover:bg-surface-subtle focus-visible:outline focus-visible:outline-2 focus-visible:outline-orange"
    >
      <div className="min-w-0 flex-1">
        <p className="truncate text-body font-semibold text-txt-primary">
          {deal.deal_name || `Deal #${deal.id}`}
        </p>
        <div className="mt-0.5 flex flex-wrap items-center gap-x-2 gap-y-1 text-body-sm text-txt-secondary">
          {deal.bank_name && <span className="truncate">{deal.bank_name}</span>}
          {purposeLabel && (
            <Badge variant="purpose" size="sm">
              {purposeLabel}
            </Badge>
          )}
          {deal.status && (
            <Badge variant="neutral" size="sm">
              {deal.status}
            </Badge>
          )}
        </div>
        <div className="mt-1 flex flex-wrap items-baseline gap-x-3 gap-y-0.5 text-body-sm text-txt-secondary">
          {deal.financing_amount != null && deal.financing_amount > 0 && (
            <span className="font-medium text-txt-primary">
              <FinancialNumber
                value={deal.financing_amount}
                format="currency"
              />
            </span>
          )}
          {typeof deal.term_sheets_count === 'number' &&
            deal.term_sheets_count > 0 && (
              <span>
                {deal.term_sheets_count} term sheet
                {deal.term_sheets_count === 1 ? '' : 's'}
              </span>
            )}
        </div>
      </div>
      {typeof deal.score === 'number' && (
        <div className="shrink-0">
          <ScoreDisplay
            score={Math.round(deal.score)}
            size="sm"
            showLabel={false}
          />
        </div>
      )}
    </button>
  );
}
